import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useMobileNavHeader } from '@/hooks/useMobileNavHeader';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import { useBuilderMode } from '@/lib/crm/hooks/useBuilderMode';
import { SettingsSideNavigation } from '@/pages/dashboard/settings/settings-side-navigation';

import { AuthenticatedRoute } from '../allauth_auth/routing.jsx';
import { DisconnectedAccountBanner } from './DisconnectedAccountBanner';
import ConversationHistoryPanel from './navigation/ConversationHistoryPanel';
import { useConversationHistoryCollapsed } from './navigation/hooks/useConversationHistoryCollapsed';
import SideNavigation from './navigation/SideNavigation';
import { NewVersionBanner } from './NewVersionBanner';
import { OfflineIndicator } from './OfflineIndicator';
import { TeamGuard } from './OrgGuard';
import SystemStatusBanner from './SystemStatusBanner';

interface DashboardLayoutProps {
  children: React.ReactNode;
  /** Skip auth/guard wrapper when parent already has TeamGuard */
  skipGuard?: boolean;
}

/**
 * Inner layout component that handles content padding based on sidebars.
 */
function DashboardLayoutInner({
  children,
  showConversationHistory,
  historyCollapsed,
  onToggleHistoryCollapsed,
  isSettingsPage,
  settingsBackPath,
}: {
  children: React.ReactNode;
  /**
   * Whether the past-conversations nav is mounted. Drives both the panel
   * itself and the content padding that makes room for it — the two must move
   * together or the content indents against an absent panel.
   */
  showConversationHistory: boolean;
  /**
   * Collapsed state of that nav (user toggle, persisted to localStorage).
   * Same coupling as above: the panel's width and the content padding must
   * move together.
   */
  historyCollapsed: boolean;
  onToggleHistoryCollapsed: () => void;
  isSettingsPage: boolean;
  settingsBackPath: string | null;
}) {
  // Track viewport height (handles mobile browser chrome)
  useViewportHeight();

  // Determine if mobile nav header is visible (drives top padding)
  const { showMobileHeader } = useMobileNavHeader();

  // Enable padding transition only after initial mount to prevent jank.
  const [enableTransition, setEnableTransition] = useState(false);
  useEffect(() => {
    setEnableTransition(true);
  }, []);

  // Static literals per branch (Tailwind JIT needs whole class strings); the
  // 40px arm is the collapsed history strip's width.
  const historyPadding = historyCollapsed
    ? 'lg:pl-[calc(var(--sidebar-width,68px)+40px)]'
    : 'lg:pl-[calc(var(--sidebar-width,68px)+220px)]';

  return (
    <div className='min-h-full gradient-bg'>
      <SideNavigation hideDesktopSidebar={isSettingsPage} />
      {isSettingsPage && <SettingsSideNavigation backPath={settingsBackPath} />}
      {showConversationHistory && (
        <ConversationHistoryPanel
          collapsed={historyCollapsed}
          onToggleCollapsed={onToggleHistoryCollapsed}
        />
      )}

      {/* Main content area with dynamic padding for both sidebars */}
      {/* Height set by useViewportHeight hook via --app-viewport-height CSS property (tracks visualViewport for mobile browser chrome) */}
      {/* Shell shifts by --app-viewport-offset-top (margin-top) to stay aligned with the visual viewport when iOS scrolls the layout viewport for the on-screen keyboard */}
      {/* flex col ensures children can fill available height via flex-1 */}
      {/* Top padding matches mobile nav header height — only applied when header is visible */}
      <div
        className={`dashboard-content relative h-[var(--app-viewport-height,100dvh)] mt-[var(--app-viewport-offset-top,0px)] pl-0 ${showMobileHeader ? 'pt-10 lg:pt-0' : 'pt-0'} pb-[env(safe-area-inset-bottom)] flex flex-col ${
          // Settings pages own their scrolling in settings-layout's inner
          // content div (flex-1 min-h-0 overflow-y-auto). Letting this outer
          // container ALSO scroll produces a double-scroll on mobile, where
          // the 100dvh fallback can exceed the visualViewport. Clip here so
          // the inner container is the sole scroll owner on settings.
          isSettingsPage ? 'overflow-hidden' : 'overflow-y-auto'
        } ${enableTransition ? 'transition-[padding] duration-200' : ''} ${
          showConversationHistory
            ? historyPadding
            : 'lg:pl-[var(--sidebar-width,224px)]'
        }`}
      >
        <SystemStatusBanner />
        <OfflineIndicator />
        <DisconnectedAccountBanner />
        <NewVersionBanner />
        {children}
      </div>
    </div>
  );
}

export default function DashboardLayout({
  children,
  skipGuard = false,
}: DashboardLayoutProps) {
  const location = useLocation();
  const { isBuilderMode } = useBuilderMode();

  // Show conversation panel on conversation pages (but not on history page)
  const isConversationPage =
    location.pathname.includes('/conversations/') &&
    !location.pathname.endsWith('/conversations/history');

  // Builder mode keeps the object in focus, so the past-conversations nav is
  // suppressed. This hides the history LIST only — the current thread's
  // messages still render, since they are the conversation being built.
  const showConversationHistory = isConversationPage && !isBuilderMode;

  const {
    collapsed: historyCollapsed,
    toggleCollapsed: toggleHistoryCollapsed,
  } = useConversationHistoryCollapsed();

  // Settings pages swap the primary sidebar for the settings sidebar
  const isSettingsPage =
    location.pathname.includes('/settings') ||
    location.pathname.endsWith('/profile');

  // Track last non-settings path so the settings back button is contextual.
  const lastNonSettingsPath = useRef<string | null>(null);
  useEffect(() => {
    if (!isSettingsPage) {
      lastNonSettingsPath.current = `${location.pathname}${location.search}${location.hash}`;
    }
  }, [location.pathname, location.search, location.hash, isSettingsPage]);

  const content = (
    <DashboardLayoutInner
      showConversationHistory={showConversationHistory}
      historyCollapsed={historyCollapsed}
      onToggleHistoryCollapsed={toggleHistoryCollapsed}
      isSettingsPage={isSettingsPage}
      settingsBackPath={lastNonSettingsPath.current}
    >
      {children}
    </DashboardLayoutInner>
  );

  // When skipGuard is true (e.g., nested under route-level TeamGuard), render directly
  if (skipGuard) {
    return content;
  }

  // Otherwise wrap with auth/guard for routes that use DashboardLayout directly
  return (
    <AuthenticatedRoute>
      <TeamGuard>{content}</TeamGuard>
    </AuthenticatedRoute>
  );
}
